/** @jsxImportSource @emotion/react */
import * as React from "react";
import { connect } from "react-redux";
import { defaultMergeProps } from "../redux/sugar";
import { IKON_MODAL } from "./ikonModal";
import { IAppState } from "../redux/IAppState";
import PassPhotoModal from "../../guest/guestPhoto/PassPhotoModal";
import PassPrintModal from "../../guest/entitlements/media/passPrinting/PassPrintModal";
import AssociateRfidModal from "../../guest/entitlements/media/assocRfid/AssociateRfidModal";
import { DtwEntitlementModal } from "../../guest/entitlements/redemption/directToWindow/DtwEntitlementModal";
import AddToHotlistModal from "../../guest/hotlist/AddToHotlistModal";
import RemoveFromHotlistModal from "../../guest/hotlist/RemoveFromHotlistModal";
import { AddGuestCommentModal } from "../../guest/guestComments/AddGuestCommentModal";
import { ViewDaysModal } from "../../guest/entitlements/redemption/viewDays/ViewDaysModal";
import { GuestCommentHistoryModal } from "../../guest/guestComments/GuestCommentHistoryModal";
import { dismissModalAction } from "./dismissModalAction";
import WaiverModal from "../../guest/entitlements/waivers/WaiverModal";
import GroupPrintModal from "../../guest/entitlements/media/passPrinting/GroupPrintModal";
import MediaHistoryModal from "../../guest/entitlements/media/mediaHistory/MediaHistoryModal";
import { FnFEntitlementModal } from "../../guest/entitlements/redemption/friendsAndFamily/FnFModal";
import { PaymentMethodModal } from "../../guest/guestPayment/PaymentMethodModal";
import { HydrationReportModal } from "../../reports/hydrationErrorReport/GuestHydrationErrorReportModal";
import { HotlistReportModal } from "../../reports/hotlistReport/HotlistReportModal";
import { WeeklyVisitorReportsModal } from "../../reports/weeklyVisitorReport/WeeklyVisitorReportsModal";
import { AccessIndividualEditModal } from "../../productAdministration/AccessIndividualEditModal";
import { AccessMultiCreateModal } from "../../productAdministration/AccessMultiCreateModel";
import { AccessMultiEditModal } from "../../productAdministration/AccessMultiEditModal";

/**
 * ModalHost: a component that listens for new modal data in the Redux store, and turns those datas into modal views.
 * This component usually spreads the modal extras onto the modal view as props, but the case may vary from modal to
 * modal.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState) => ({ currentModal: state.currentModal }),
  (dispatch) => ({ dismissModal: () => dispatch(dismissModalAction()) }),
  defaultMergeProps,
  {
    areStatesEqual: (next: IAppState, prev: IAppState) =>
      next.currentModal === prev.currentModal,
  },
)(
  class ModalHost extends React.PureComponent<{
    currentModal?: {
      type: IKON_MODAL;
      extras?: any;
    };
    dismissModal?: () => void;
  }> {
    private onDismiss = () => this.props.dismissModal!();

    public render = () => {
      const extras = {
        ...(this.props.currentModal!.extras || {}),
        onDismiss: this.onDismiss,
      };

      switch (this.props.currentModal!.type) {
        case IKON_MODAL.GUEST_PHOTO:
          return <PassPhotoModal {...extras} />;

        case IKON_MODAL.GUEST_PRINTER:
          return <PassPrintModal {...extras} />;

        case IKON_MODAL.GUEST_RFID:
          return <AssociateRfidModal {...extras} />;

        case IKON_MODAL.GUEST_WAIVER:
          return <WaiverModal {...extras} />;

        case IKON_MODAL.GUEST_FNF:
          return <FnFEntitlementModal {...extras} />;

        case IKON_MODAL.GUEST_DTW:
          return <DtwEntitlementModal {...extras} />;

        case IKON_MODAL.GUEST_ADD_TO_HOTLIST:
          return <AddToHotlistModal {...extras} />;

        case IKON_MODAL.GUEST_REMOVE_FROM_HOTLIST:
          return <RemoveFromHotlistModal {...extras} />;

        case IKON_MODAL.GUEST_COMMENT_HISTORY:
          return <GuestCommentHistoryModal {...extras} />;

        case IKON_MODAL.GUEST_ADD_COMMENT:
          return <AddGuestCommentModal {...extras} />;

        case IKON_MODAL.GUEST_VIEW_DAYS:
          return <ViewDaysModal {...extras} />;

        case IKON_MODAL.GUEST_MEDIA_HISTORY:
          return <MediaHistoryModal {...extras} />;

        case IKON_MODAL.GUEST_GROUP_PRINT:
          return <GroupPrintModal {...extras} />;

        case IKON_MODAL.GUEST_PAYMENT_METHOD:
          return <PaymentMethodModal {...extras} />;

        case IKON_MODAL.GUEST_HYDRATION_REPORT:
          return <HydrationReportModal {...extras} />;

        case IKON_MODAL.HOTLIST_REPORT:
          return <HotlistReportModal {...extras} />;

        case IKON_MODAL.WEEKLY_VISITOR_REPORTS:
          return <WeeklyVisitorReportsModal {...extras} />;

        case IKON_MODAL.ACCESS_INDIVIDUAL_EDIT:
          return <AccessIndividualEditModal {...extras} />;

        case IKON_MODAL.ACCESS_MULTI_CREATE:
          return <AccessMultiCreateModal {...extras} />;

        case IKON_MODAL.ACCESS_MULTI_EDIT:
          return <AccessMultiEditModal {...extras} />;

        case IKON_MODAL.NONE:
        default:
          return null;
      }
    };
  },
);
